import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

/**
 * Route-chunk loading with stale-deploy recovery.
 *
 * The PWA worker ships `skipWaiting` + `clientsClaim` + `cleanupOutdatedCaches`
 * (`vite.config.ts`, `registerType: 'autoUpdate'`). When a deploy lands the new
 * worker activates immediately, claims already-open tabs, and deletes the
 * previous precache — while those tabs are still running the PREVIOUS entry
 * chunk. The next lazy route such a tab requests is then gone from the cache
 * AND gone from the server (Pages only keeps the current build's
 * content-hashed assets), so the dynamic import rejects. Every lazy route in
 * the tab is dead until the user manually clears the cache, which is why this
 * presents as "a lot of pages are broken" rather than one bad screen.
 *
 * The SPA fallback in `public/_redirects` answers the missing chunk with
 * `index.html` at 200 instead of a 404, so the failure surfaces as a MIME/parse
 * error — hence the engine-specific wording matched in `isStaleChunkError`.
 *
 * Recovery: drop the stale worker plus its caches and reload once. The guard
 * flag means a chunk that is genuinely broken fails loudly on the second
 * attempt instead of pinning the tab in a reload loop.
 *
 * ## The offline case — recovery must NOT run (2026-08-03)
 *
 * Since the precache became an allow-list of the eight offline-first screens
 * (`vite.config.ts`, `OFFLINE_FIRST_ROUTES`), a desk-only route's chunk is
 * runtime-cached: it exists on the server but is not on the device until
 * someone opens it once with a connection. Opening `/settings` for the first
 * time with no signal therefore rejects the dynamic import with the SAME
 * message a stale deploy produces — there is no way to tell them apart from the
 * error alone.
 *
 * Running recovery there would be catastrophic rather than merely useless: it
 * unregisters the service worker and deletes every cache, then reloads. Offline
 * that leaves no worker to answer the navigation and no network to ask, so an
 * installed app that was working a second ago is dead until the device finds
 * signal — and the user's queued offline work is behind a shell that no longer
 * loads. The trigger would be a floor worker tapping a settings link in a dead
 * zone.
 *
 * So: when the origin cannot be reached, this throws instead. The route error
 * boundary then says the screen has not been downloaded yet, which is both true
 * and actionable, and the rest of the installed app is untouched.
 *
 * "Cannot be reached" is TWO questions, because `navigator.onLine` answers only
 * the easy one. It is false only when the OS knows there is no link at all; a
 * captive portal, a plant Wi-Fi with a dead uplink, and a server that is simply
 * down all report `true`. Those are the dead-zone cases that matter most here,
 * so a failed import also probes the origin before anything is destroyed.
 */

const RELOAD_GUARD_KEY = 'floorfile:chunk-reload'

/** sessionStorage throws in some privacy modes; recovery must not depend on it. */
function readGuard(): boolean {
  try {
    return window.sessionStorage.getItem(RELOAD_GUARD_KEY) !== null
  } catch {
    return false
  }
}

function setGuard(): void {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, '1')
  } catch {
    // Best effort. Without the flag the worst case is a second reload attempt,
    // which still terminates: the purge below only leaves work to do once.
  }
}

function clearGuard(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_GUARD_KEY)
  } catch {
    // Ignored — see setGuard.
  }
}

/**
 * Every engine words this differently, and none of them expose a stable error
 * code. Matching the message is the only option available.
 */
export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return (
    // Chrome / Edge
    message.includes('Failed to fetch dynamically imported module') ||
    // Firefox
    message.includes('error loading dynamically imported module') ||
    // Safari
    message.includes('Importing a module script failed') ||
    // Any engine when the SPA fallback returns index.html for a missing chunk
    message.includes('is not a valid JavaScript MIME type') ||
    message.includes('Expected a JavaScript module script')
  )
}

/**
 * Removes the worker and caches pinning this tab to the dead build, so the
 * reload fetches a fresh shell. `registerSW.js` re-registers on the next load,
 * so offline support returns on its own.
 */
async function purgeStaleShell(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((registration) => registration.unregister()))
    }
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((key) => caches.delete(key)))
    }
  } catch {
    // Best effort — a failed purge must still let the reload proceed, since a
    // plain reload alone often clears the condition.
  }
}

/**
 * The browser side effects, behind a seam. The unit suite runs in Node with no
 * DOM, and the interesting behaviour here is the decision (recover once vs.
 * rethrow), not the DOM calls — so the decision has to be reachable without one.
 */
export interface ChunkRecovery {
  hasReloaded: () => boolean
  markReloaded: () => void
  clearReloaded: () => void
  purge: () => Promise<void>
  reload: () => void
  /** `navigator.onLine`, behind the seam. False means "definitely no network".
   * True is only a hint — a captive portal, a dead uplink and a stopped server
   * all report true — which is why `reachable()` exists below. */
  isOnline: () => boolean
  /** Can this origin actually be reached right now? Asked before destroying the
   * installed app, because `navigator.onLine` cannot answer it. */
  reachable: () => Promise<boolean>
}

export const browserRecovery: ChunkRecovery = {
  hasReloaded: readGuard,
  markReloaded: setGuard,
  clearReloaded: clearGuard,
  purge: purgeStaleShell,
  reload: () => window.location.reload(),
  isOnline: () => navigator.onLine !== false,
  reachable: probeOrigin,
}

/**
 * One HEAD request to the origin, cache-busted so neither the HTTP cache nor
 * the service worker can answer it. A `fetch` is not a navigation request, so
 * the SPA navigation fallback does not intercept it either — a reply means the
 * server is genuinely there.
 *
 * Any failure counts as unreachable, including the timeout: this decides
 * whether to DELETE the offline app, and the safe answer when the answer is
 * unknown is "don't".
 */
async function probeOrigin(): Promise<boolean> {
  const abort = new AbortController()
  const timer = setTimeout(() => abort.abort(), 3000)
  try {
    const response = await fetch(`/?chunk-probe=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-store',
      signal: abort.signal,
    })
    return response.ok
  } catch {
    return false
  } finally {
    clearTimeout(timer)
  }
}

/**
 * "This screen is not on the device and there is no network to fetch it from."
 *
 * A distinct type rather than the raw import rejection, because the route error
 * boundary has to tell this apart from a genuine crash: the wording, and
 * whether "Reload" is worth offering, differ entirely.
 */
export class OfflineChunkError extends Error {
  constructor(cause: unknown) {
    super('This screen has not been downloaded to this device yet.', { cause })
    this.name = 'OfflineChunkError'
  }
}

export function isOfflineChunkError(error: unknown): error is OfflineChunkError {
  // Name, not `instanceof`: an error crossing a chunk boundary can meet a
  // second copy of the class, and this is the wrong place to be subtle.
  return error instanceof Error && error.name === 'OfflineChunkError'
}

/**
 * `React.lazy`'s loader plus stale-deploy recovery. Exported for the suite;
 * application code goes through `lazyRoute`.
 */
export async function loadRouteChunk<T>(
  factory: () => Promise<{ default: T }>,
  recovery: ChunkRecovery = browserRecovery,
): Promise<{ default: T }> {
  try {
    const module = await factory()
    // A successful load means this tab is on the live build; re-arm recovery
    // for whatever the NEXT deploy does to it.
    recovery.clearReloaded()
    return module
  } catch (error) {
    // A chunk that is genuinely missing from a CURRENT build must still reach
    // the error boundary — recovering twice would be an unbreakable reload loop.
    if (!isStaleChunkError(error) || recovery.hasReloaded()) throw error

    // Offline: the chunk is runtime-cached and has never been fetched. Purging
    // here would delete the working offline app to fix a screen that was never
    // on the device — see the header. `navigator.onLine` first because it is
    // free and often decisive; the probe because it is the only one of the two
    // that can tell a dead uplink or a stopped server from a stale deploy, and
    // those look identical from the import rejection alone.
    if (!recovery.isOnline() || !(await recovery.reachable())) {
      throw new OfflineChunkError(error)
    }

    recovery.markReloaded()
    // The reload must happen even if the purge does not. By this point the
    // guard is already set, so letting a purge failure propagate would strand
    // the tab on the dead build with recovery permanently spent — and a plain
    // reload clears the condition on its own often enough to be worth trying.
    await recovery.purge().catch(() => undefined)
    recovery.reload()

    // The reload replaces this document. Never resolving keeps the Suspense
    // fallback on screen for the interim rather than flashing a crash screen
    // the user cannot act on.
    return new Promise<never>(() => {})
  }
}

/**
 * Drop-in replacement for `React.lazy` for route-level code splitting.
 * Behaviour is identical except that a stale-deploy chunk failure recovers
 * itself instead of hitting the feature error boundary.
 */
export function lazyRoute<
  // Mirrors React.lazy's own constraint. Narrowing this to `ComponentType`
  // (i.e. `ComponentType<{}>`) silently rejects every route that takes props,
  // so the bound has to stay as loose as the function it stands in for.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ComponentType<any>,
>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() => loadRouteChunk(factory))
}
